import {
  Component,
  computed,
  inject,
  input,
  signal,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../../../shared/icons/icon.component';
import { IconNameType } from '../../../shared/icons/icon-name-type.enum';
import { OrderableSpecialStepAlternativeItem } from '../../models/menu/orderable-special-step-alternative-item.model';
import { Picture } from '../../models/menu/picture.model';
import { SpecialWizardService } from '../../services/special-wizard.service';
import { SizePrice } from '../../models/menu/size-price.model';
import { OrderableItem } from '../../models/menu/orderable-item.model';
import { OrderedItem } from '../../models/ordering/ordered-item.model';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { AuditService } from '../../services/audit.service';
import { Utils } from '../../utils/utils';
import { ImageFallbackDirective } from '../../../shared/directives/image-fallback.directive';

@Component({
  selector: 'cwo-special-item',
  standalone: true,
  imports: [CommonModule, IconComponent, TranslocoPipe, ImageFallbackDirective],
  templateUrl: './special-item.component.html',
  styleUrl: './special-item.component.scss',
})
export class SpecialItemComponent {
  specialWizardService = inject(SpecialWizardService);
  private auditService = inject(AuditService);
  private translocoService = inject(TranslocoService);
  item = input.required<OrderableSpecialStepAlternativeItem>();
  selected = input(false);
  readonly itemSelected = output<{
    orderableItem: OrderableItem;
    orderedItem: OrderedItem;
}>();
  
  loading = signal(false);
  iconNameType = IconNameType;

  availableSizePrices = computed(() => {
    return this.item()?.availableSizePrices;
  });

  hasCalories = computed(() => {
    const sizePrices = this.item()?.sizePrices;
    if (!sizePrices) return null;
    const sizeWithCalories = sizePrices.find((s: SizePrice) => s.caloriesFrom != null );
    if (!sizeWithCalories) return null;
    return Utils.getCalorieDisplay(this.item(), sizeWithCalories); 
  });

  selectedSizePrice() {
    return this.item().selectedSize ?? this.availableSizePrices()?.find(sp => sp.status === 'AVAILABLE' && sp.isWebDefault)?.size;
  }

  itemImg = computed<Picture | null>(() => {
    if (this.item()?.pictures?.length) {
      return this.item().pictures ? this.item().pictures![0] : null;
    } else {
      return null;
    }
  });

  smallestNoticePeriod = computed<string | undefined>(() => {
    const sizePrices = this.item()?.sizePrices;
    if (!sizePrices) return undefined;
    return Utils.getNoticePeriodLabel(sizePrices, (key, params) => this.translocoService.translate(key, params));
  });

  isItemAvailable = computed(() => {
    return !this.item()?.pricePeriodMessage;
  });

  onSizeChanged(event: Event, sizePrice: SizePrice) {
    event.preventDefault();
    event.stopPropagation();
    this.item().selectedSize = sizePrice.size;
  }

  onItemSelected() {
      this.loading.set(true);
      this.specialWizardService
      .getOrderableItemFromUrl(this.item(), this.selectedSizePrice())
      .subscribe({
        next: ({orderableItem, orderedItem}) => {
          this.loading.set(false);
          this.auditService.createAudit(() => `Add ${orderedItem?.item} ${orderedItem?.size !=='Size' ? `of size ${orderedItem?.size} ` : ''}button pressed`);
          const ordered = this.selected() ? this.specialWizardService.editingSpecialWizard()!.currentStep!.selectedOrderedItem()! : orderedItem;
          this.itemSelected.emit({orderableItem, orderedItem: ordered});
        },
        error: () => {
          this.loading.set(false);
        },
      });
    }
}
